/**
 * Pipeline - Capital deployment pipeline for tracking entry (buy) and exit (sell/trim) candidates
 * 
 * Lifecycle: research -> ready -> active -> complete | canceled
 * Lateral: paused (from any stage, resumes to saved stage)
 */

import { useState, useEffect } from 'react';
import { 
  Target, TrendingUp, TrendingDown, Plus, X, Check, 
  ChevronDown, ChevronRight, RefreshCw, AlertCircle,
  DollarSign, Hash, ArrowUpRight, Pause, Play, XCircle
} from 'lucide-react';
import { fetchApi } from '../api/client';

function formatCurrency(value) {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return '';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

const STATUS_COLORS = {
  research: 'bg-slate-600/50 text-slate-400',
  ready: 'bg-sky-500/20 text-sky-400',
  active: 'bg-emerald-500/20 text-emerald-400',
  complete: 'bg-green-500/20 text-green-400',
  paused: 'bg-amber-500/20 text-amber-400',
  canceled: 'bg-red-500/20 text-red-400',
};

export default function Pipeline() {
  const [items, setItems] = useState([]);
  const [proximityAlerts, setProximityAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [addingTierTo, setAddingTierTo] = useState(null);
  const [filter, setFilter] = useState('all');
  const [pipelineTab, setPipelineTab] = useState('all');
  const [accounts, setAccounts] = useState([]);
  const [promotingTier, setPromotingTier] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('');
  
  const [newItem, setNewItem] = useState({ ticker: '', direction: 'entry', notes: '' });
  const [newTier, setNewTier] = useState({ target_price: '', dollar_amount: '', quantity: '', notes: '' });

  const fetchPipeline = async () => {
    try {
      const data = await fetchApi('/pipeline');
      setItems(data);
    } catch (err) {
      console.error('Error fetching pipeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProximityAlerts = async () => {
    try {
      const data = await fetchApi('/pipeline/alerts/proximity?threshold_pct=15');
      setProximityAlerts(data.alerts || []);
    } catch (err) {
      console.error('Error fetching proximity:', err);
    }
  };

  const fetchAccounts = async () => {
    try {
      const data = await fetchApi('/accounts');
      setAccounts(data.filter(a => !a.is_closed));
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  useEffect(() => {
    fetchPipeline();
    fetchProximityAlerts();
    fetchAccounts();
  }, []);

  const handlePromoteTier = async () => {
    if (!promotingTier || !selectedAccount) return;
    
    try {
      const data = await fetchApi(`/pipeline/tiers/${promotingTier.tier.id}/promote`, {
        method: 'POST',
        body: JSON.stringify({ account_id: selectedAccount }),
      });
      alert(`Trade plan created: ${data.trade_plan.name}`);
      setPromotingTier(null);
      setSelectedAccount('');
      fetchPipeline();
    } catch (err) {
      console.error('Error promoting tier:', err);
      alert(`Error: ${err.message || 'Failed to create trade plan'}`);
    }
  };

  const toggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!newItem.ticker || !newItem.direction) return;
    
    try {
      await fetchApi('/pipeline', {
        method: 'POST',
        body: JSON.stringify(newItem),
      });
      setNewItem({ ticker: '', direction: 'entry', notes: '' });
      setShowAddForm(false);
      fetchPipeline();
    } catch (err) {
      console.error('Error adding item:', err);
    }
  };

  const handleAddTier = async (e, itemId) => {
    e.preventDefault();
    if (!newTier.target_price) return;
    
    try {
      await fetchApi(`/pipeline/${itemId}/tiers`, {
        method: 'POST',
        body: JSON.stringify({
          target_price: parseFloat(newTier.target_price),
          dollar_amount: newTier.dollar_amount ? parseFloat(newTier.dollar_amount) : null,
          quantity: newTier.quantity ? parseFloat(newTier.quantity) : null,
          notes: newTier.notes || null,
        }),
      });
      setNewTier({ target_price: '', dollar_amount: '', quantity: '', notes: '' });
      setAddingTierTo(null);
      fetchPipeline();
      fetchProximityAlerts();
    } catch (err) {
      console.error('Error adding tier:', err);
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!confirm('Remove this pipeline item and all its tiers?')) return;
    
    try {
      await fetchApi(`/pipeline/${itemId}`, { method: 'DELETE' });
      fetchPipeline();
      fetchProximityAlerts();
    } catch (err) {
      console.error('Error removing item:', err);
    }
  };

  const handleRemoveTier = async (tierId) => {
    try {
      await fetchApi(`/pipeline/tiers/${tierId}`, { method: 'DELETE' });
      fetchPipeline();
      fetchProximityAlerts();
    } catch (err) {
      console.error('Error removing tier:', err);
    }
  };

  const handleTriggerTier = async (tierId) => {
    try {
      await fetchApi(`/pipeline/tiers/${tierId}/trigger`, { method: 'POST' });
      fetchPipeline();
    } catch (err) {
      console.error('Error triggering tier:', err);
    }
  };

  const handleExecuteTier = async (tierId) => {
    try {
      await fetchApi(`/pipeline/tiers/${tierId}/execute`, { method: 'POST' });
      fetchPipeline();
    } catch (err) {
      console.error('Error executing tier:', err);
    }
  };

  const filteredItems = items.filter(item => {
    if (filter !== 'all' && item.direction !== filter) return false;
    if (pipelineTab !== 'all') {
      const status = (item.status || '').toLowerCase();
      if (pipelineTab !== status) return false;
    }
    return true;
  });

  const entryCount = items.filter(i => i.direction === 'entry').length;
  const exitCount = items.filter(i => i.direction === 'exit').length;
  
  const statusCounts = {
    research: items.filter(i => i.status === 'research').length,
    ready: items.filter(i => i.status === 'ready').length,
    active: items.filter(i => i.status === 'active').length,
    paused: items.filter(i => i.status === 'paused').length,
    complete: items.filter(i => i.status === 'complete').length,
  };

  const getProximityColor = (pctFromCurrent, direction) => {
    if (pctFromCurrent === undefined || pctFromCurrent === null) return 'text-slate-400';
    const distance = Math.abs(pctFromCurrent);
    if (distance <= 5) return 'text-emerald-400 font-medium';
    if (distance <= 10) return 'text-amber-400';
    if (distance <= 20) return 'text-slate-300';
    return 'text-slate-500';
  };

  const TierRow = ({ tier, item }) => {
    const isClose = Math.abs(tier.pct_from_current || 100) <= 10;
    
    return (
      <div className={`flex items-center gap-3 py-2 px-3 rounded ${
        tier.canceled ? 'bg-red-500/5 opacity-50 line-through' :
        tier.executed ? 'bg-slate-700/30 opacity-60' :
        tier.triggered ? 'bg-amber-500/10' :
        isClose ? 'bg-emerald-500/10' : 'bg-slate-800/50'
      }`}>
        <div className="w-6 text-center text-xs text-slate-500">
          T{tier.tier_number}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className={`font-mono font-medium ${tier.canceled ? 'text-slate-500' : 'text-slate-200'}`}>
              {formatCurrency(tier.target_price)}
            </span>
            {!tier.canceled && tier.pct_from_current !== undefined && (
              <span className={`text-xs ${getProximityColor(tier.pct_from_current, item.direction)}`}>
                ({formatPercent(tier.pct_from_current)})
              </span>
            )}
            {isClose && !tier.triggered && !tier.executed && !tier.canceled && (
              <AlertCircle className="w-3 h-3 text-emerald-400" />
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
            {tier.dollar_amount && (
              <span className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                {formatCurrency(tier.dollar_amount)}
              </span>
            )}
            {tier.quantity && (
              <span className="flex items-center gap-1">
                <Hash className="w-3 h-3" />
                {tier.quantity} shares
              </span>
            )}
            {tier.notes && <span className="italic">{tier.notes}</span>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {tier.canceled ? (
            <span className="text-xs text-red-400 flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Canceled
            </span>
          ) : tier.executed ? (
            <span className="text-xs text-emerald-400 flex items-center gap-1">
              <Check className="w-3 h-3" /> Executed
            </span>
          ) : tier.triggered ? (
            <>
              <button
                onClick={() => setPromotingTier({ tier, item })}
                className="text-xs px-2 py-1 bg-sky-500/20 text-sky-400 rounded hover:bg-sky-500/30 flex items-center gap-1"
                title="Create trade plan from this tier"
              >
                <ArrowUpRight className="w-3 h-3" />
                Promote
              </button>
              <button
                onClick={() => handleExecuteTier(tier.id)}
                className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30"
              >
                Mark Executed
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setPromotingTier({ tier, item })}
                className="p-1 text-slate-500 hover:text-sky-400 hover:bg-slate-700 rounded"
                title="Promote to trade plan"
              >
                <ArrowUpRight className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleTriggerTier(tier.id)}
                className="p-1 text-slate-500 hover:text-amber-400 hover:bg-slate-700 rounded"
                title="Mark as triggered"
              >
                <Target className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleRemoveTier(tier.id)}
                className="p-1 text-slate-500 hover:text-red-400 hover:bg-slate-700 rounded"
                title="Remove tier"
              >
                <X className="w-3 h-3" />
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const PipelineItem = ({ item }) => {
    const isExpanded = expandedItems.has(item.id);
    const isEntry = item.direction === 'entry';
    const activeTiers = item.tiers?.filter(t => !t.executed) || [];
    const closestTier = activeTiers.length > 0 
      ? activeTiers.reduce((closest, tier) => {
          const distance = Math.abs(tier.pct_from_current || 100);
          const closestDistance = Math.abs(closest.pct_from_current || 100);
          return distance < closestDistance ? tier : closest;
        })
      : null;

    const isTerminal = item.status === 'complete' || item.status === 'canceled';

    return (
      <div className={`border rounded-lg overflow-hidden ${
        isTerminal ? 'border-slate-600/30 opacity-70' :
        isEntry ? 'border-blue-500/30' : 'border-purple-500/30'
      }`}>
        <div 
          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-700/30 ${
            isTerminal ? 'bg-slate-800/30' :
            isEntry ? 'bg-blue-500/5' : 'bg-purple-500/5'
          }`}
          onClick={() => toggleExpand(item.id)}
        >
          <button className="text-slate-400">
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          
          <div className={`p-1.5 rounded ${isEntry ? 'bg-blue-500/20' : 'bg-purple-500/20'}`}>
            {isEntry ? (
              <TrendingDown className="w-4 h-4 text-blue-400" />
            ) : (
              <TrendingUp className="w-4 h-4 text-purple-400" />
            )}
          </div>
          
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-200">{item.ticker}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${
                isEntry ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
              }`}>
                {isEntry ? 'BUY' : 'SELL'}
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[item.status] || 'bg-slate-600 text-slate-400'}`}>
                {item.status}
                {item.status === 'paused' && item.paused_from && (
                  <span className="ml-1 opacity-70">(was {item.paused_from})</span>
                )}
              </span>
            </div>
            {item.notes && (
              <div className="text-xs text-slate-500 mt-0.5 truncate max-w-xs">
                {item.notes}
              </div>
            )}
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {item.replacement_for && (
                <span className="text-xs text-slate-500">
                  replaces {item.replacement_for}
                </span>
              )}
              {item.category_fit && (
                <span className="text-xs text-slate-500">
                  {item.category_fit}
                </span>
              )}
            </div>
          </div>
          
          <div className="text-right">
            <div className="text-sm font-mono text-slate-300">
              {formatCurrency(item.current_price)}
            </div>
            {closestTier && (
              <div className={`text-xs ${getProximityColor(closestTier.pct_from_current, item.direction)}`}>
                Target: {formatCurrency(closestTier.target_price)}
              </div>
            )}
          </div>
          
          <div className="text-xs text-slate-500">
            {activeTiers.length} tier{activeTiers.length !== 1 ? 's' : ''}
          </div>
          
          {!isTerminal && (
            <button
              onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id); }}
              className="p-1 text-slate-500 hover:text-red-400 hover:bg-slate-700 rounded"
              title="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {isExpanded && (
          <div className="border-t border-slate-700/50 p-3 bg-slate-800/30">
            {item.tiers?.length > 0 ? (
              <div className="space-y-2">
                {item.tiers.map(tier => (
                  <TierRow key={tier.id} tier={tier} item={item} />
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500 text-center py-2">
                No price tiers defined
              </div>
            )}
            
            {/* Add Tier Form */}
            {!isTerminal && (addingTierTo === item.id ? (
              <form onSubmit={(e) => handleAddTier(e, item.id)} className="mt-3 p-3 bg-slate-700/30 rounded">
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Target Price *"
                    value={newTier.target_price}
                    onChange={(e) => setNewTier({ ...newTier, target_price: e.target.value })}
                    className="text-sm bg-slate-700 border-slate-600 rounded px-2 py-1"
                    required
                  />
                  <input
                    type="number"
                    step="0.01"
                    placeholder="$ Amount"
                    value={newTier.dollar_amount}
                    onChange={(e) => setNewTier({ ...newTier, dollar_amount: e.target.value })}
                    className="text-sm bg-slate-700 border-slate-600 rounded px-2 py-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <input
                    type="number"
                    step="0.001"
                    placeholder="Shares"
                    value={newTier.quantity}
                    onChange={(e) => setNewTier({ ...newTier, quantity: e.target.value })}
                    className="text-sm bg-slate-700 border-slate-600 rounded px-2 py-1"
                  />
                  <input
                    type="text"
                    placeholder="Notes"
                    value={newTier.notes}
                    onChange={(e) => setNewTier({ ...newTier, notes: e.target.value })}
                    className="text-sm bg-slate-700 border-slate-600 rounded px-2 py-1"
                  />
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="btn btn-primary text-sm px-3 py-1">
                    Add Tier
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setAddingTierTo(null)}
                    className="btn btn-secondary text-sm px-3 py-1"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : (
              <button
                onClick={() => setAddingTierTo(item.id)}
                className="mt-3 w-full py-2 text-sm text-slate-400 hover:text-slate-200 hover:bg-slate-700/50 rounded border border-dashed border-slate-600 flex items-center justify-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Price Tier
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
          <Target className="w-5 h-5 text-cyan-400" />
          Pipeline
          {proximityAlerts.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">
              {proximityAlerts.length} near target
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm bg-slate-700 border-slate-600 rounded px-2 py-1"
          >
            <option value="all">All ({items.length})</option>
            <option value="entry">Entry ({entryCount})</option>
            <option value="exit">Exit ({exitCount})</option>
          </select>
          <button
            onClick={() => { fetchPipeline(); fetchProximityAlerts(); }}
            disabled={loading}
            className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="p-1.5 text-slate-400 hover:text-emerald-400 hover:bg-slate-700 rounded transition-colors"
            title="Add pipeline item"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Pipeline Status Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-slate-700/50 pb-2">
        {[
          { key: 'all', label: 'All', count: items.length },
          { key: 'research', label: 'Research', count: statusCounts.research },
          { key: 'ready', label: 'Ready', count: statusCounts.ready },
          { key: 'active', label: 'Active', count: statusCounts.active },
          { key: 'paused', label: 'Paused', count: statusCounts.paused },
          { key: 'complete', label: 'Complete', count: statusCounts.complete },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setPipelineTab(tab.key)}
            className={`text-sm px-3 py-1.5 rounded-lg transition-colors ${
              pipelineTab === tab.key
                ? 'bg-slate-700 text-slate-200'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-1.5 text-xs text-slate-500">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Add Item Form */}
      {showAddForm && (
        <form onSubmit={handleAddItem} className="mb-4 p-3 bg-slate-700/30 rounded-lg">
          <div className="grid grid-cols-3 gap-2 mb-2">
            <input
              type="text"
              placeholder="Ticker *"
              value={newItem.ticker}
              onChange={(e) => setNewItem({ ...newItem, ticker: e.target.value.toUpperCase() })}
              className="text-sm bg-slate-700 border-slate-600 rounded px-2 py-1.5"
              required
            />
            <select
              value={newItem.direction}
              onChange={(e) => setNewItem({ ...newItem, direction: e.target.value })}
              className="text-sm bg-slate-700 border-slate-600 rounded px-2 py-1.5"
            >
              <option value="entry">Entry (Buy)</option>
              <option value="exit">Exit (Sell)</option>
            </select>
            <input
              type="text"
              placeholder="Notes/Thesis"
              value={newItem.notes}
              onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
              className="text-sm bg-slate-700 border-slate-600 rounded px-2 py-1.5"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary text-sm px-3 py-1">
              Add to Pipeline
            </button>
            <button 
              type="button" 
              onClick={() => setShowAddForm(false)}
              className="btn btn-secondary text-sm px-3 py-1"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Proximity Alerts */}
      {proximityAlerts.length > 0 && (
        <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <div className="text-sm font-medium text-emerald-400 mb-2 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Near Target Prices
          </div>
          <div className="space-y-1">
            {proximityAlerts.slice(0, 5).map((alert, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <span className="text-slate-300">{alert.ticker}</span>
                <span className="text-slate-400">
                  T{alert.tier_number}: {formatCurrency(alert.target_price)}
                </span>
                <span className={alert.at_target ? 'text-emerald-400 font-medium' : 'text-amber-400'}>
                  {alert.at_target ? 'AT TARGET' : `${alert.pct_away.toFixed(1)}% away`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="spinner w-6 h-6"></div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No pipeline items</p>
          <p className="text-sm mt-1">Add stocks to track entry or exit opportunities</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map(item => (
            <PipelineItem key={item.id} item={item} />
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-700 text-xs text-slate-500 text-center">
          {entryCount} entry • {exitCount} exit • {items.reduce((sum, i) => sum + (i.tiers?.length || 0), 0)} tiers
        </div>
      )}

      {/* Promote to Trade Plan Modal */}
      {promotingTier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-200 mb-4 flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-sky-400" />
              Promote to Trade Plan
            </h3>
            
            <div className="space-y-4">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-200">{promotingTier.item.ticker}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    promotingTier.item.direction === 'entry' 
                      ? 'bg-blue-500/20 text-blue-400' 
                      : 'bg-purple-500/20 text-purple-400'
                  }`}>
                    {promotingTier.item.direction === 'entry' ? 'BUY' : 'SELL'}
                  </span>
                </div>
                <div className="text-sm text-slate-400 mt-1">
                  Target: {formatCurrency(promotingTier.tier.target_price)}
                  {promotingTier.tier.quantity && ` • ${promotingTier.tier.quantity} shares`}
                  {promotingTier.tier.dollar_amount && ` • ${formatCurrency(promotingTier.tier.dollar_amount)}`}
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">Select Account</label>
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="w-full bg-slate-700 border-slate-600 rounded px-3 py-2 text-slate-200"
                >
                  <option value="">Choose an account...</option>
                  {accounts.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name} ({acc.account_type})</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handlePromoteTier}
                  disabled={!selectedAccount}
                  className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Trade Plan
                </button>
                <button
                  onClick={() => { setPromotingTier(null); setSelectedAccount(''); }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
